import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { GroupValidationBloc } from '@kp/auth/group-validation.bloc';
import { EligibilityVerification } from '@kp/eligibility/eligibility-verification.model';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { EligibilityVerificationType } from '@ktypes/enums';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface EmailFormValues {
  email: string;
}

export interface EmployeeFormValues {
  employeeId: string;
}

export interface MemberFormValues {
  memberId: string;
}

export interface StudentEmailFormValues {
  studentEmail: string;
}

export interface StudentFormValues {
  studentId: string;
}

export interface SingleEligibilityFormValues {
  placeholder?: string;
  controlName?: EligibilitySingleFormControlName;
  control?: UntypedFormControl;
  label?: string;
}

export enum EligibilitySingleFormControlName {
  email = 'email',
  employeeId = 'employeeId',
  memberId = 'memberId',
  studentEmail = 'studentEmail',
  studentId = 'studentId',
}

@Component({
  selector: 'kp-eligibility-single-value',
  templateUrl: './eligibility-single-value.component.html',
  styleUrls: ['./eligibility-single-value.component.scss'],
})
export class EligibilitySingleValueComponent implements OnInit, OnDestroy {
  @Input() eligibilityVerificationType: EligibilityVerificationType;
  @Input() error: boolean;
  @Input() loading: boolean;

  @Output() errorEmitter = new EventEmitter<boolean>();
  @Output() loadingEmitter = new EventEmitter<boolean>();

  eligibilityForm: UntypedFormGroup;
  formValues: SingleEligibilityFormValues;

  private _destroy$ = new Subject<void>();

  // Email form control
  private _email: UntypedFormControl;

  // EmployeeId form control
  private _employeeId: UntypedFormControl;

  // MemberId form control
  private _memberId: UntypedFormControl;

  // Student email form control
  private _studentEmail: UntypedFormControl;

  // StudentId form control
  private _studentId: UntypedFormControl;

  constructor(
    private _analyticsBloc: AnalyticsBloc,
    public groupValidationBloc: GroupValidationBloc,
    private _onboardingBloc: OnboardingBloc,
    private _router: Router
  ) {}

  ngOnInit(): void {
    switch (this.eligibilityVerificationType) {
      case EligibilityVerificationType.email:
        this._email = new UntypedFormControl(
          this._email,
          Validators.pattern(/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
        );
        this.formValues = {
          placeholder: 'Enter your work email',
          controlName: EligibilitySingleFormControlName.email,
          control: this._email,
          label: 'Work Email',
        };
        break;
      case EligibilityVerificationType.employee_identifier:
        this._employeeId = new UntypedFormControl(this._employeeId);
        this.formValues = {
          placeholder: 'Enter your employee ID',
          controlName: EligibilitySingleFormControlName.employeeId,
          control: this._employeeId,
          label: 'Employee ID',
        };
        break;
      case EligibilityVerificationType.member_identifier:
        this._memberId = new UntypedFormControl(this._memberId);
        this.formValues = {
          placeholder: 'Enter your member ID',
          controlName: EligibilitySingleFormControlName.memberId,
          control: this._memberId,
          label: 'Member ID',
        };
        break;
      case EligibilityVerificationType.student_email:
        this._studentEmail = new UntypedFormControl(
          this._studentEmail,
          Validators.pattern(/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)
        );
        this.formValues = {
          placeholder: 'Enter your student email',
          controlName: EligibilitySingleFormControlName.studentEmail,
          control: this._studentEmail,
          label: 'Student Email',
        };
        break;
      case EligibilityVerificationType.student_identifier:
        this._studentId = new UntypedFormControl(this._studentId);
        this.formValues = {
          placeholder: 'Enter your student ID',
          controlName: EligibilitySingleFormControlName.studentId,
          control: this._studentId,
          label: 'Student ID',
        };
        break;
    }
    this._setSingleValueForm();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this._email = null;
    this._employeeId = null;
    this._memberId = null;
    this._studentEmail = null;
    this._studentId = null;
    this.error = false;
    this.loading = false;
    this.errorEmitter.emit(false);
    this.loadingEmitter.emit(false);
  }

  onSubmit(
    formValues: EmailFormValues | EmployeeFormValues | MemberFormValues | StudentFormValues | StudentEmailFormValues
  ) {
    if (this.eligibilityForm.valid) {
      this.loading = true;
      this.loadingEmitter.emit(true);
      this._onboardingBloc.verifyEligibility(getEligibilityVerification(formValues));
    }
  }

  switchToAnotherMethod() {
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: AnalyticsPageName.eligibility,
        event: AnalyticEvent.eligibility_method_switched,
      })
    );
    // Needed to clear out error message when switching between types
    this._onboardingBloc.clearEligibilityStream();
  }

  private _setSingleValueForm(): void {
    this.eligibilityForm = new UntypedFormGroup({
      [this.formValues?.controlName]: this.formValues?.control,
    });
    this.eligibilityForm.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      if (value != null) {
        this.error = false;
        this.loading = false;
        this.errorEmitter.emit(false);
        this.loadingEmitter.emit(false);
      }
    });
  }
}

function getEligibilityVerification(
  formValues: EmailFormValues | EmployeeFormValues | MemberFormValues | StudentEmailFormValues | StudentFormValues
): EligibilityVerification {
  if ('email' in formValues) {
    return new EligibilityVerification().deserialize({
      verificationType: EligibilityVerificationType.email,
      email: formValues?.email.trim(),
    });
  } else if ('employeeId' in formValues) {
    return new EligibilityVerification().deserialize({
      verificationType: EligibilityVerificationType.employee_identifier,
      eligibilityId: formValues?.employeeId.trim(),
    });
  } else if ('memberId' in formValues) {
    return new EligibilityVerification().deserialize({
      verificationType: EligibilityVerificationType.member_identifier,
      eligibilityId: formValues?.memberId.trim(),
    });
  } else if ('studentEmail' in formValues) {
    return new EligibilityVerification().deserialize({
      verificationType: EligibilityVerificationType.student_email,
      email: formValues?.studentEmail.trim(),
    });
  } else if ('studentId' in formValues) {
    return new EligibilityVerification().deserialize({
      verificationType: EligibilityVerificationType.student_identifier,
      eligibilityId: formValues?.studentId.trim(),
    });
  } else {
    console.warn('No eligibility verification object');
    return null;
  }
}
